import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Search, ChevronRight, Phone, Mail, X } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { useTasks } from '../hooks/useTasks';
import { useFolders } from '../hooks/useFolders';
import DashboardSidebar from './DashboardSidebar';
import TaskCard from './TaskCard';
import DeleteModal from './DeleteModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import { Task } from '../services/task';

interface DashboardProps {
  onOpenNewRecording: () => void;
  onOpenWorkspace: (taskId?: string) => void;
  onLogout: () => void;
  onUpgrade: () => void;
  onOpenInvite: () => void;
  onOpenShare: () => void;
}

export default function Dashboard(props: DashboardProps) {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState<string>('all');

  const { tasks, setTasks, loading, error, fetchTasks } = useTasks({
    activeTab,
    onTaskCompleted: (title) => {
      showToast(language === 'zh' ? `「${title}」已完成` : `"${title}" completed`, 'success');
      if (document.hidden) flashTitle(language === 'zh' ? '✅ 任务完成' : '✅ Task Done');
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(language === 'zh' ? '任务完成' : 'Task Completed', {
          body: language === 'zh' ? `「${title}」已完成处理` : `"${title}" has been processed`,
          icon: '/icon.png',
        });
      }
    },
    onTaskFailed: (title) => {
      showToast(language === 'zh' ? `「${title}」处理失败` : `"${title}" failed`, 'error');
      if (document.hidden) flashTitle(language === 'zh' ? '❌ 任务失败' : '❌ Task Failed');
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(language === 'zh' ? '任务失败' : 'Task Failed', {
          body: language === 'zh' ? `「${title}」处理失败` : `"${title}" processing failed`,
          icon: '/icon.png',
        });
      }
    }
  });

  const {
    folders, setFolders, foldersLoading, setFoldersLoading,
    editingFolderId, setEditingFolderId,
    editingFolderName, setEditingFolderName,
    draggingTaskId, setDraggingTaskId,
    dragOverFolderId, setDragOverFolderId,
    folderInputRef, displayFolderName, handleDeleteFolder
  } = useFolders(language);

  const [userInfo, setUserInfo] = useState<{ username?: string; email?: string; phone?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [showBindModal, setShowBindModal] = useState<'phone' | 'email' | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: number; title: string; type: 'success' | 'error' }>>([]);

  const [activeFileMenu, setActiveFileMenu] = useState<string | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState<string | null>(null);

  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');
  const taskTitleInputRef = useRef<HTMLInputElement>(null);

  const [deleteState, setDeleteState] = useState<{ isOpen: boolean, type: 'File' | 'Folder' | null, id: string | null, title: string }>({
    isOpen: false, type: null, id: null, title: ''
  });

  const [bindValue, setBindValue] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUserInfo(JSON.parse(storedUser)); } catch (e) { }
    }
  }, []);

  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    const timer = setTimeout(() => setShowNotificationModal(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const closeMenu = () => { setActiveFileMenu(null); setShowFolderPicker(null); };
    if (activeFileMenu) {
      const timeout = setTimeout(() => document.addEventListener('click', closeMenu), 10);
      return () => { clearTimeout(timeout); document.removeEventListener('click', closeMenu); };
    }
  }, [activeFileMenu]);

  useEffect(() => {
    const closeSettingsPopup = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.settings-popup-container')) {
        setShowSettingsPopup(false);
      }
    };
    if (showSettingsPopup) {
      const timer = setTimeout(() => document.addEventListener('click', closeSettingsPopup), 10);
      return () => { clearTimeout(timer); document.removeEventListener('click', closeSettingsPopup); };
    }
  }, [showSettingsPopup]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCodeSent(false);
    }
  }, [countdown]);

  const toastIdRef = useRef(0);
  const showToast = useCallback((title: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, title, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const titleFlashRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const originalTitleRef = useRef(document.title);
  const flashTitle = useCallback((msg: string) => {
    if (titleFlashRef.current) return;
    originalTitleRef.current = document.title;
    let show = true;
    titleFlashRef.current = setInterval(() => {
      document.title = show ? msg : originalTitleRef.current;
      show = !show;
    }, 1000);
    const stopFlash = () => {
      if (titleFlashRef.current) { clearInterval(titleFlashRef.current); titleFlashRef.current = null; }
      document.title = originalTitleRef.current;
      window.removeEventListener('focus', stopFlash);
    };
    window.addEventListener('focus', stopFlash);
    setTimeout(stopFlash, 10000);
  }, []);

  const handleSendCode = async () => {
    if (!bindValue.trim()) return;
    setCodeSent(true);
    setCountdown(60);
  };

  const handleConfirmBind = async () => {
    if (!bindValue.trim() || !verificationCode.trim()) return;
    const updatedUser = {
      ...userInfo,
      [showBindModal === 'phone' ? 'phone' : 'email']: bindValue
    };
    setUserInfo(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setShowBindModal(null); setBindValue(''); setVerificationCode(''); setCodeSent(false);
  };

  const handleToggleFavorite = async (taskId: string, currentFavorite: boolean) => {
    try {
      const { updateFavorite } = await import('../services/task');
      await updateFavorite(taskId, !currentFavorite);
      setTasks(prev => prev.map(t => t.taskId === taskId ? { ...t, isFavorite: !currentFavorite } : t));
      setFolders(prev => prev.map(f => ({
        ...f,
        tasks: f.tasks?.map(t => t.taskId === taskId ? { ...t, isFavorite: !currentFavorite } : t)
      })));
    } catch (e: any) {
      console.error('Failed to update favorite:', e);
    }
  };

  const startEditingTaskTitle = (taskId: string, currentTitle: string) => {
    setEditingTaskId(taskId);
    setEditingTaskTitle(currentTitle);
    setTimeout(() => taskTitleInputRef.current?.focus(), 50);
  };

  const saveTaskTitle = async () => {
    if (!editingTaskId) return;
    const trimmedTitle = editingTaskTitle.trim();
    if (trimmedTitle) {
      try {
        const { updateTaskTitle } = await import('../services/task');
        await updateTaskTitle(editingTaskId, trimmedTitle);
        setTasks(prev => prev.map(t => t.taskId === editingTaskId ? { ...t, title: trimmedTitle } : t));
        setFolders(prev => prev.map(f => ({
          ...f,
          tasks: f.tasks?.map(t => t.taskId === editingTaskId ? { ...t, title: trimmedTitle } : t)
        })));
      } catch (e) {
        console.error('Failed to update task title:', e);
      }
    }
    setEditingTaskId(null);
  };
  const cancelEditingTaskTitle = () => { setEditingTaskId(null); setEditingTaskTitle(''); };

  const startEditingFolder = (folderId: string, currentName: string) => {
    setEditingFolderId(folderId);
    setEditingFolderName(displayFolderName(currentName));
    setTimeout(() => folderInputRef.current?.focus(), 50);
  };

  const saveEditingFolder = async () => {
    if (!editingFolderId || !editingFolderName.trim()) { setEditingFolderId(null); return; }
    try {
      let finalName = editingFolderName.trim();
      const enMatch = finalName.match(/^New Folder(\d*)$/);
      if (enMatch) finalName = `新建文件夹${enMatch[1]}`;
      const otherNames = new Set(folders.filter(f => f.folderId !== editingFolderId).map(f => f.name));
      if (otherNames.has(finalName)) {
        let n = 1;
        while (otherNames.has(`${finalName}(${n})`)) n++;
        finalName = `${finalName}(${n})`;
      }
      const { renameFolder } = await import('../services/folder');
      await renameFolder(editingFolderId, finalName);
      setFolders(prev => prev.map(f => f.folderId === editingFolderId ? { ...f, name: finalName } : f));
    } catch (e) { console.error('Failed to rename folder:', e); }
    setEditingFolderId(null);
  };
  const cancelEditingFolder = () => { setEditingFolderId(null); setEditingFolderName(''); };

  const onCreateFolder = async () => {
    try {
      const baseName = '新建文件夹';
      const existingNames = new Set(folders.map(f => f.name));
      let folderName = baseName;
      if (existingNames.has(folderName)) {
        let n = 2;
        while (existingNames.has(`${baseName}${n}`)) n++;
        folderName = `${baseName}${n}`;
      }
      const { createFolder } = await import('../services/folder');
      const response = await createFolder(folderName);
      const newFolder = { ...response, expanded: false, sortOrder: folders.length, taskCount: 0 };
      setFolders([newFolder, ...folders]);
    } catch (e) { console.error('Failed to create folder:', e); }
  };

  const onToggleFolder = async (folderId: string) => {
    const folder = folders.find(f => f.folderId === folderId);
    if (!folder) return;
    if (!folder.expanded && !folder.tasks) {
      setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, expanded: true, tasksLoading: true } : f));
      try {
        const { getTasksByFolder } = await import('../services/folder');
        const response = await getTasksByFolder(folderId);
        setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, tasks: response || [], tasksLoading: false } : f));
      } catch (e) {
        setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, tasks: [], tasksLoading: false } : f));
      }
    } else {
      setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, expanded: !f.expanded } : f));
    }
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggingTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    const card = (e.currentTarget as HTMLElement).closest('[data-card-id]') as HTMLElement;
    if (card) {
      const rect = card.getBoundingClientRect();
      e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
    }
  };
  const handleDragEnd = () => { setDraggingTaskId(null); setDragOverFolderId(null); };

  const handleDragOverFolder = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    const targetFolder = folders.find(f => f.folderId === folderId);
    if (draggingTaskId && targetFolder?.tasks?.some(t => t.taskId === draggingTaskId)) e.dataTransfer.dropEffect = 'none';
    else e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  };
  const handleDragLeaveFolder = () => setDragOverFolderId(null);

  const _moveTaskToFolderLocal = async (taskId: string, folderId: string) => {
    const targetFolder = folders.find(f => f.folderId === folderId);
    if (targetFolder?.tasks?.some(t => t.taskId === taskId)) return;
    const taskFromMain = tasks.find(t => t.taskId === taskId);
    let movedTask: Task | undefined = taskFromMain;
    if (!taskFromMain) {
      for (const folder of folders) {
        const taskInFolder = folder.tasks?.find(t => t.taskId === taskId);
        if (taskInFolder) { movedTask = taskInFolder; break; }
      }
    }
    if (!movedTask) return;
    setFolders(prev => prev.map(f => {
      const hasTask = f.tasks?.some(t => t.taskId === taskId);
      let newTasks = f.tasks;
      let newTaskCount = f.taskCount;
      if (hasTask) {
        newTasks = f.tasks?.filter(t => t.taskId !== taskId);
        newTaskCount = Math.max(0, (f.taskCount || 0) - 1);
      }
      if (f.folderId === folderId) {
        newTasks = [...(newTasks || []), movedTask!];
        newTaskCount = (newTaskCount || 0) + 1;
      }
      return { ...f, tasks: newTasks, taskCount: newTaskCount };
    }));
    try {
      const { moveTaskToFolder } = await import('../services/folder');
      await moveTaskToFolder(taskId, folderId);
    } catch (e: any) { console.error('Failed to move task:', e); }
  };

  const handleDropToFolder = async (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    setDraggingTaskId(null);
    setDragOverFolderId(null);
    if (taskId) await _moveTaskToFolderLocal(taskId, folderId);
  };
  const handleMoveToFolderFromMenu = async (taskId: string, folderId: string) => {
    setActiveFileMenu(null); setShowFolderPicker(null);
    await _moveTaskToFolderLocal(taskId, folderId);
  };

  const confirmDelete = async () => {
    if (deleteState.type === 'Folder' && deleteState.id) {
      try {
        await handleDeleteFolder(deleteState.id);
        if (activeTab === `folder_${deleteState.id}`) setActiveTab('all');
      } catch (e) { console.error('Failed to delete folder:', e); }
    } else if (deleteState.type === 'File' && deleteState.id) {
      try {
        const { deleteTask } = await import('../services/task');
        await deleteTask(deleteState.id);
        setTasks(prev => prev.filter(t => t.taskId !== deleteState.id));
        setFolders(prev => prev.map(f => ({
          ...f, tasks: f.tasks?.filter(t => t.taskId !== deleteState.id)
        })));
      } catch (e) { console.error('Failed to delete task:', e); }
    }
    setDeleteState({ isOpen: false, type: null, id: null, title: '' });
  };

  const getTabTitle = () => {
    if (activeTab === 'favorites') return t.dashboard.favorites;
    if (activeTab.startsWith('folder_')) {
      const folderId = activeTab.replace('folder_', '');
      const folder = folders.find(f => f.folderId === folderId);
      return folder?.name || t.dashboard.folders;
    }
    return t.dashboard.allRecordings;
  };

  const displayFiles = tasks.filter(file => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    return (
      (file.title || '').toLowerCase().includes(q) ||
      (file.summary || '').toLowerCase().includes(q) ||
      (file.speakers || []).some(s => s.toLowerCase().includes(q)) ||
      (file.keywords || []).some(kw => kw.toLowerCase().includes(q))
    );
  });

  return (
    <div className="flex min-h-screen bg-surface font-sans text-brand-900">
      <DeleteModal isOpen={deleteState.isOpen} onClose={() => setDeleteState({ ...deleteState, isOpen: false })} onConfirm={confirmDelete} title={deleteState.title} itemType={deleteState.type || 'Item'} />
      <DashboardSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        folders={folders}
        onCreateFolder={onCreateFolder}
        onToggleFolder={onToggleFolder}
        editingFolderId={editingFolderId}
        editingFolderName={editingFolderName}
        setEditingFolderName={setEditingFolderName}
        saveEditingFolder={saveEditingFolder}
        cancelEditingFolder={cancelEditingFolder}
        startEditingFolder={startEditingFolder}
        folderInputRef={folderInputRef}
        displayFolderName={displayFolderName}
        draggingTaskId={draggingTaskId}
        dragOverFolderId={dragOverFolderId}
        handleDragOverFolder={handleDragOverFolder}
        handleDragLeaveFolder={handleDragLeaveFolder}
        handleDropToFolder={handleDropToFolder}
        setDeleteState={setDeleteState}
        onLogout={props.onLogout}
        onUpgrade={props.onUpgrade}
        onOpenInvite={props.onOpenInvite}
        userInfo={userInfo}
        showSettingsPopup={showSettingsPopup}
        setShowSettingsPopup={setShowSettingsPopup}
        setShowChangePassword={setShowChangePassword}
        setShowBindModal={setShowBindModal}
        setShowNotificationModal={setShowNotificationModal}
        onOpenWorkspace={props.onOpenWorkspace}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleToggleFavorite={handleToggleFavorite}
      />

      <main className="flex-1 ml-72 p-10 min-w-0 bg-surface">
        <header className="flex justify-between items-center mb-10">
          <div className="flex items-center gap-2">
            <span className="text-brand-900 hover:text-ink-dark cursor-pointer transition-colors font-serif font-bold text-lg tracking-tight">{t.workspace.back}</span>
            <ChevronRight size={18} className="text-ink-muted mx-1" />
            <span className="text-brand-900 font-serif font-bold text-lg tracking-tight">{getTabTitle()}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted group-focus-within:text-brand-900 transition-colors" size={16} />
              <input type="text" placeholder={t.dashboard.searchPlaceholder} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2.5 bg-white border border-surface-border rounded-xl w-64 focus:w-80 transition-all outline-none focus:border-surface-divider focus:ring-4 focus:ring-surface-border/50 text-sm shadow-sm placeholder:text-ink-muted text-brand-900" />
            </div>
            <button onClick={props.onOpenNewRecording} className="h-12 bg-brand-900 hover:bg-brand-800 text-white rounded-full pl-1.5 pr-6 flex items-center gap-3 transition-all shadow-deep hover:-translate-y-0.5 active:translate-y-0 group">
              <div className="w-7 h-7 rounded-full bg-brand-700 flex items-center justify-center text-white shadow-inner group-hover:scale-105 transition-transform shrink-0"><Plus size={12} strokeWidth={3} /></div>
              <span className="font-bold text-sm tracking-wide">{t.dashboard.newRecording}</span>
            </button>
          </div>
        </header>

        <div className="flex flex-col gap-3">
          {displayFiles.map((task) => (
            <TaskCard
              key={task.taskId}
              task={task}
              onOpen={props.onOpenWorkspace}
              onDelete={(taskId, title) => setDeleteState({ isOpen: true, type: 'File', id: taskId, title })}
              onToggleFavorite={handleToggleFavorite}
              onMoveToFolder={(taskId, folderId) => handleMoveToFolderFromMenu(taskId, folderId)}
              isMenuOpen={activeFileMenu === task.taskId}
              onMenuToggle={() => setActiveFileMenu(activeFileMenu === task.taskId ? null : task.taskId)}
              isDragging={draggingTaskId === task.taskId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              editingTaskId={editingTaskId}
              editingTaskTitle={editingTaskTitle}
              setEditingTaskTitle={setEditingTaskTitle}
              startEditingTaskTitle={startEditingTaskTitle}
              saveTaskTitle={saveTaskTitle}
              cancelEditingTaskTitle={cancelEditingTaskTitle}
              taskTitleInputRef={taskTitleInputRef}
              showFolderPicker={showFolderPicker}
              setShowFolderPicker={setShowFolderPicker}
              folders={folders}
              displayFolderName={displayFolderName}
            />
          ))}
        </div>
      </main>

      {/* Notifications */}
      {showNotificationModal && (
        <div className="fixed bottom-20 left-4 z-[9999] animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="bg-white rounded-2xl shadow-2xl py-4 px-5 w-64 relative border border-surface-border">
            <button onClick={() => setShowNotificationModal(false)} className="absolute top-3 right-3 text-ink-muted hover:text-brand-900 transition-colors"><X size={14} /></button>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-9 h-9 rounded-full bg-surface-border flex items-center justify-center shrink-0">
                <Bell className="w-4.5 h-4.5 text-brand-900" size={16} />
              </div>
              <h3 className="text-sm font-bold text-brand-900">{language === 'zh' ? '开启任务通知' : 'Enable Notifications'}</h3>
            </div>
            <p className="text-xs text-ink-secondary mb-4 leading-relaxed">{language === 'zh' ? '开启通知后，任务完成时会第一时间提醒你。' : 'Get notified when your tasks complete.'}</p>
            <button onClick={() => { Notification.requestPermission(); setShowNotificationModal(false); }} className="w-full py-2 bg-brand-900 text-white rounded-full font-semibold hover:bg-brand-800 transition-colors mb-1.5 text-xs">{language === 'zh' ? '开启通知' : 'Enable'}</button>
            <button onClick={() => { setShowNotificationModal(false); localStorage.setItem('notification-dismissed', '1'); }} className="w-full py-1 text-xs text-ink-muted hover:text-brand-900 transition-colors">{language === 'zh' ? '暂不开启' : 'Not Now'}</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showBindModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowBindModal(null)}></div>
          <div className="relative w-full max-w-md bg-white rounded-card shadow-2xl p-6 animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowBindModal(null)} className="absolute top-4 right-4 p-2 text-ink-muted hover:text-brand-900 hover:bg-surface rounded-full transition-colors"><X size={20} /></button>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-surface-border rounded-xl flex items-center justify-center">
                {showBindModal === 'phone' ? <Phone size={24} className="text-brand-900" /> : <Mail size={24} className="text-brand-900" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-900">{showBindModal === 'phone' ? (language === 'zh' ? '绑定手机号' : 'Bind Phone Number') : (language === 'zh' ? '绑定邮箱' : 'Bind Email')}</h3>
                <p className="text-sm text-ink-secondary">{language === 'zh' ? '绑定后可用于登录和找回密码' : 'Can be used for login and password recovery'}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-2">{showBindModal === 'phone' ? (language === 'zh' ? '手机号' : 'Phone Number') : (language === 'zh' ? '邮箱地址' : 'Email Address')}</label>
                <input type={showBindModal === 'phone' ? 'tel' : 'email'} value={bindValue} onChange={(e) => setBindValue(e.target.value)} placeholder={showBindModal === 'phone' ? (language === 'zh' ? '请输入手机号' : 'Enter phone number') : (language === 'zh' ? '请输入邮箱' : 'Enter email')} className="w-full px-4 py-3 bg-surface border border-surface-border rounded-xl outline-none focus:border-brand-700 focus:ring-2 focus:ring-surface-border text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900 mb-2">{language === 'zh' ? '验证码' : 'Verification Code'}</label>
                <div className="flex gap-3">
                  <input type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder={language === 'zh' ? '请输入验证码' : 'Enter code'} className="flex-1 px-4 py-3 bg-surface border border-surface-border rounded-xl outline-none focus:border-brand-700 focus:ring-2 focus:ring-surface-border text-sm" />
                  <button onClick={handleSendCode} disabled={!bindValue.trim() || countdown > 0} className={`px-4 py-3 rounded-xl text-sm font-bold whitespace-nowrap transition-colors ${!bindValue.trim() || countdown > 0 ? 'bg-surface-border text-ink-muted cursor-not-allowed' : 'bg-brand-900 text-white hover:bg-brand-800'}`}>{countdown > 0 ? `${countdown}s` : (codeSent ? (language === 'zh' ? '重新发送' : 'Resend') : (language === 'zh' ? '发送验证码' : 'Send Code'))}</button>
                </div>
              </div>
            </div>
            <button onClick={handleConfirmBind} disabled={!bindValue.trim() || !verificationCode.trim()} className={`w-full mt-6 py-3.5 rounded-xl text-sm font-bold transition-colors ${!bindValue.trim() || !verificationCode.trim() ? 'bg-surface-border text-ink-muted cursor-not-allowed' : 'bg-brand-900 text-white hover:bg-brand-800'}`}>{language === 'zh' ? '确认绑定' : 'Confirm Binding'}</button>
          </div>
        </div>
      )}
      {showChangePassword && <ForgotPasswordModal mode="change" onClose={() => setShowChangePassword(false)} onBackToLogin={() => setShowChangePassword(false)} />}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className={`pointer-events-auto flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl border backdrop-blur-sm animate-in slide-in-from-right-5 fade-in duration-300 min-w-[320px] max-w-[420px] ${toast.type === 'success' ? 'bg-white/95 border-brand-700/20 text-brand-900' : 'bg-white/95 border-red-200 text-red-700'}`}>
            <div className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</div>
            <span className="text-sm font-medium leading-snug">{toast.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bell({ size, className }: { size: number, className: string }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>; }
